import type { Revision } from '~/@entities/SchoolSystem'
import type { Student, StudentId } from '~/@entities/Student'
import type {
  CommonCloudFrontUrlResponse,
  CommonResponse,
  CommonResponseListMonthlyAvailableReportResponse,
  CommonResponseListReportResponse,
  CommonResponseRecentlyReportResponse,
  CommonResponseReportCreateResponse,
  CommonResponseReportResponse,
  CommonResponseSetString,
  CommonResponseStudentAchievementReportResponse,
  CustomReportCreationRequest,
  CustomReportResponse,
  CustomReportScoringGenerationRequest,
  CustomReportScoringHistoryCountResponse,
  CustomReportScoringHistoryResponse,
  CustomReportShareToParentAppRequest,
  CustomReportShareToParentSmsRequest,
  CustomReportUnshareToParentAppRequest,
  CustomReportUpdateRequest,
  DeleteByReportIdParams,
  DownloadAchievementReportParams,
  DownloadCasAutoTestReportParams,
  DownloadCustomReportsByParams,
  DownloadRealTimeReportByParams,
  DownloadWorksheetReportParams,
  GetCustomReportsByParams,
  GetCustomReportScoringHistoryByParams,
  GetRealTimeReportMetricByParams,
  GetRealTimeReportsByParams,
  GetRealTimeReportSettingsByParams,
  GetRecentReportYearMonthByParams,
  GetReportCreatableStudentParams,
  GroupCasAutoTestReportRequest,
  GroupExamReportRequest,
  GroupWorksheetReportRequest,
  IsRecreatedReportParams,
  Pageable,
  RealTimeReportLinkGenerationRequest,
  RealTimeReportResponse,
  RealTimeReportSettingResponse,
  RealTimeReportSettingUpdateRequest,
  RealTimeReportShareToParentAppRequest,
  RealTimeReportShareToParentSmsRequest,
  ReportLinkResponse,
  ReportMetricResponse,
  ReportOpenToParentUpdateRequest,
  ReportScoringGenerationResponse,
  ReportScoringWithSettingGenerationResponse,
  ReportUpdateRequest,
  SliceCustomReportResponse,
  StudentAchievementReportRequest,
  UpdateRealTimeReportSettingParams,
} from '~/api-spec/__generated__/main'
import { httpClient, type HttpFile, type RequestConfig, type RequestMeta } from '~/http/client'
import type { WithDefaultedOptionals, WithNullableOptionals } from '~/http/request'
import type { Slice } from '~/schema/slice'

import {
  adaptCustomReportEditSource,
  adaptCustomReportList,
  adaptCustomReports,
  adaptLegacyReport,
  adaptLegacyReports,
  adaptLegacyReportSaveResult,
  adaptMonthlyReportAvailabilities,
  adaptRealTimeReports,
  adaptRealTimeReportSetting,
  adaptRecentReportYearMonth,
  adaptReportAiComment,
  adaptReportAiSession,
  adaptReportDraft,
  adaptReportFile,
  adaptReportMetric,
  adaptReportScoringHistory,
  adaptReportScoringHistoryCount,
  adaptReportShareLink,
  adaptReportStudentIds,
  adaptStudentAchievementReports,
} from './adapters'
import type {
  CustomReport,
  CustomReportEditSource,
  LegacyReport,
  LegacyReportSaveResult,
  MonthlyReportAvailability,
  RealTimeReport,
  RealTimeReportSetting,
  RecentReportYearMonth,
  ReportAiComment,
  ReportAiSession,
  ReportComparisonGroup,
  ReportDocument,
  ReportDraft,
  ReportFile,
  ReportMetric,
  ReportScoringHistory,
  ReportScoringHistoryCount,
  ReportShareLink,
  StudentAchievementReports,
} from './schemas'

// 이 컨트롤러(ReportControllerV2)는 CommonResponse 봉투 없이 본문을 바로 낸다.

// 서버는 조회 조건과 보고서 설정의 선택 항목을 nullable(`Int?`·`String?`·`Set<…>?`)로 받지만 스펙은 optional 로만
// 그린다. 2026-10-07 백엔드 develop 의 report/controller/ReportControllerV2.kt 와 request/*.kt 로 확인.
export type RealTimeReportListParams = WithNullableOptionals<GetRealTimeReportsByParams>
export type RealTimeReportMetricParams = GetRealTimeReportMetricByParams
export type RealTimeReportSettingParams = WithNullableOptionals<GetRealTimeReportSettingsByParams>
export type RealTimeReportSettingTarget = WithNullableOptionals<UpdateRealTimeReportSettingParams>
export type RealTimeReportSettingBody = WithNullableOptionals<RealTimeReportSettingUpdateRequest>
export type CustomReportListParams = Omit<
  WithNullableOptionals<GetCustomReportsByParams>,
  'pageable'
> &
  Pageable
export type ReportScoringHistoryParams = GetCustomReportScoringHistoryByParams
export type ReportFileParams = DownloadRealTimeReportByParams
export type ReportDraftBody = WithNullableOptionals<CustomReportScoringGenerationRequest>
// 보고서 본문은 채점 조회 응답을 앱이 편집해 그대로 되돌려 보내는 값이라 응답과 같은 도메인 타입(ReportDocument)을
// 싣는다. 스펙의 ReportRequestInterface 는 nullable 필드를 optional 로만 그려 응답의 null 을 받지 못한다.
export type CustomReportUpdateBody = Omit<
  WithNullableOptionals<CustomReportUpdateRequest>,
  'request'
> & { request: ReportDocument }
// 생성 요청의 학생 항목(CustomReportCreationRequest.Student)을 스펙은 이름이 같은 학생 응답 클래스로 덮어썼다.
// 서버 클래스는 수정 요청에서 기간·제목·구성을 빼고 studentId 를 더한 모양이다.
// 2026-10-07 백엔드 develop 의 report/controller/request/CustomReportCreationRequest.kt 로 확인.
export type CustomReportCreateBody = Omit<
  WithNullableOptionals<CustomReportCreationRequest>,
  'students'
> & {
  students: (Omit<CustomReportUpdateBody, 'startDate' | 'endDate' | 'title' | 'components'> & {
    studentId: Student['id']
  })[]
}
export type CustomReportShareToParentAppBody = CustomReportShareToParentAppRequest
export type CustomReportUnshareToParentAppBody = CustomReportUnshareToParentAppRequest
export type CustomReportShareToParentSmsBody = CustomReportShareToParentSmsRequest
export type RealTimeReportShareToParentAppBody = RealTimeReportShareToParentAppRequest
export type RealTimeReportShareToParentSmsBody = RealTimeReportShareToParentSmsRequest
export type RealTimeReportShareLinkBody = RealTimeReportLinkGenerationRequest

/***************** 실시간 보고서 *****************/

export const getRealTimeReports = async (
  params: RealTimeReportListParams,
  meta?: RequestMeta,
): Promise<RealTimeReport[]> =>
  adaptRealTimeReports(
    await httpClient().get<RealTimeReportResponse[]>('/v2/report/real-time', { params, meta }),
  ) ?? []

export const getRealTimeReportMetric = async (
  params: RealTimeReportMetricParams,
  meta?: RequestMeta,
): Promise<ReportMetric> =>
  adaptReportMetric(
    await httpClient().get<ReportMetricResponse>('/v2/report/real-time/metric', { params, meta }),
  )

export const getRealTimeReportSetting = async (
  params: RealTimeReportSettingParams,
  meta?: RequestMeta,
): Promise<RealTimeReportSetting> =>
  adaptRealTimeReportSetting(
    await httpClient().get<RealTimeReportSettingResponse>('/v2/report/real-time/setting', {
      params,
      meta,
    }),
  )

// 설정을 적용할 대상(학생·반·학년)은 쿼리로, 설정 값은 본문으로 보낸다
export const updateRealTimeReportSetting = async (
  target: RealTimeReportSettingTarget,
  body: RealTimeReportSettingBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put('/v2/report/real-time/setting', body, { params: target, meta })
}

export const getRealTimeReportFile = async (
  params: ReportFileParams,
  meta?: RequestMeta,
): Promise<ReportFile> =>
  adaptReportFile(
    await httpClient().get<CommonCloudFrontUrlResponse>('/v2/report/real-time/download', {
      params,
      meta,
    }),
  )

export const shareRealTimeReportToParentApp = async (
  body: RealTimeReportShareToParentAppBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/v2/report/real-time/share/parent-app', body, { meta })
}

export const shareRealTimeReportToParentSms = async (
  body: RealTimeReportShareToParentSmsBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/v2/report/real-time/share/parent-sms', body, { meta })
}

export const createRealTimeReportShareLink = async (
  body: RealTimeReportShareLinkBody,
  meta?: RequestMeta,
): Promise<ReportShareLink> =>
  adaptReportShareLink(
    await httpClient().post<ReportLinkResponse>('/v2/report/real-time/share/by-link', body, {
      meta,
    }),
  )

/***************** 만든 보고서 *****************/

/** 조건이 바뀌어 다시 조회하는 화면은 signal 로 앞선 요청을 버린다 */
export const getCustomReports = async (
  params: CustomReportListParams,
  meta?: RequestMeta,
  signal?: AbortSignal,
): Promise<Slice<CustomReport>> =>
  adaptCustomReports(
    await httpClient().get<SliceCustomReportResponse>('/v2/report/custom', {
      params,
      meta,
      signal,
    }),
  )

export const getCustomReportsByIds = async (
  ids: CustomReport['id'][],
  meta?: RequestMeta,
): Promise<CustomReport[]> =>
  adaptCustomReportList(
    await httpClient().get<CustomReportResponse[]>('/v2/report/custom/by-id', {
      params: { ids },
      meta,
    }),
  ) ?? []

export const getReportScoringHistoryCount = async (
  params: ReportScoringHistoryParams,
  meta?: RequestMeta,
): Promise<ReportScoringHistoryCount> =>
  adaptReportScoringHistoryCount(
    await httpClient().get<CustomReportScoringHistoryCountResponse>(
      '/v2/report/custom/scoring-history/count',
      { params, meta },
    ),
  )

export const getReportScoringHistory = async (
  params: ReportScoringHistoryParams,
  meta?: RequestMeta,
): Promise<ReportScoringHistory> =>
  adaptReportScoringHistory(
    await httpClient().get<CustomReportScoringHistoryResponse>(
      '/v2/report/custom/scoring-history',
      { params, meta },
    ),
  )

// 고른 학습으로 보고서 본문 초안을 만든다. 조건을 바꿔 다시 만들 때 앞선 요청을 버린다.
export const createReportDraft = async (
  body: ReportDraftBody,
  config?: Pick<RequestConfig, 'meta' | 'signal'>,
): Promise<ReportDraft> =>
  adaptReportDraft(
    await httpClient().post<ReportScoringGenerationResponse>(
      '/v2/report/custom/scoring',
      body,
      config,
    ),
  )

export const getCustomReportEditSource = async (
  reportId: CustomReport['id'],
  meta?: RequestMeta,
): Promise<CustomReportEditSource> =>
  adaptCustomReportEditSource(
    await httpClient().get<ReportScoringWithSettingGenerationResponse>(
      `/v2/report/custom/${reportId}/scoring`,
      { meta },
    ),
  )

// 응답 본문이 없다. 만든 보고서는 목록을 다시 조회해 얻는다.
export const createCustomReports = async (
  body: CustomReportCreateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/v2/report/custom', body, { meta })
}

export const updateCustomReport = async (
  reportId: CustomReport['id'],
  body: CustomReportUpdateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().put(`/v2/report/custom/${reportId}`, body, { meta })
}

export const shareCustomReportsToParentApp = async (
  body: CustomReportShareToParentAppBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/v2/report/custom/share/parent-app', body, { meta })
}

export const unshareCustomReportsToParentApp = async (
  body: CustomReportUnshareToParentAppBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/v2/report/custom/unshare/parent-app', body, { meta })
}

export const shareCustomReportsToParentSms = async (
  body: CustomReportShareToParentSmsBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/v2/report/custom/share/parent-sms', body, { meta })
}

export const createCustomReportShareLink = async (
  reportId: CustomReport['id'],
  meta?: RequestMeta,
): Promise<ReportShareLink> =>
  adaptReportShareLink(
    await httpClient().post<ReportLinkResponse>(
      `/v2/report/custom/${reportId}/share/by-link`,
      undefined,
      { meta },
    ),
  )

/***************** 구 보고서 *****************/
// 조회·생성·수정은 CommonResponse 봉투에 싸서 내고, 파일 주소(…/download)는 봉투 없이 낸다.

// 스펙은 YearMonth 를 객체로, nullable 필드를 optional 로만 그린다. 서버는 yyyy-MM 문자열과 null 을 받는다.
// 2026-10-13 백엔드 develop 의 report/controller/request/ReportUpdateRequest.kt 로 확인.
export type LegacyReportBody = Omit<WithNullableOptionals<ReportUpdateRequest>, 'yearMonth'> & {
  yearMonth?: string
}
export type LegacyReportCreateBody = { option: LegacyReportBody; studentIdList: StudentId[] }
export type LegacyReportDuplicationParams = IsRecreatedReportParams
export type RecentReportParams = GetRecentReportYearMonthByParams
// 스펙은 @ModelAttribute 로 받는 기간·개정(ReportCreatableStudentRequest)을 request 한 칸으로 그렸다. 실제로는 평평한
// 쿼리 파라미터다. 2026-10-13 백엔드 develop 의 report/controller/ReportController.kt 로 확인.
export type ReportCreatableStudentParams = Omit<GetReportCreatableStudentParams, 'request'> &
  GetReportCreatableStudentParams['request']
export type ReportOpenBody = ReportOpenToParentUpdateRequest
export type ReportsFileParams = DownloadCustomReportsByParams
// 서버 요청 클래스는 shareToCreverse 를 기본값(false)으로 두고 비교군·반·토큰을 nullable 로 받는다.
// 2026-10-13 백엔드 develop 의 report/controller/request/Group*ReportRequest.kt 로 확인.
export type WorksheetGroupReportBody = WithDefaultedOptionals<
  WithNullableOptionals<GroupWorksheetReportRequest>,
  'shareToCreverse'
>
export type CasGroupReportBody = WithDefaultedOptionals<
  WithNullableOptionals<GroupCasAutoTestReportRequest>,
  'shareToCreverse'
>
export type ExamGroupReportBody = GroupExamReportRequest
// 스펙은 @ModelAttribute 로 받는 대상 id 를 request 한 칸으로 그렸다. 실제로는 평평한 쿼리 파라미터다.
export type WorksheetReportPdfParams = Omit<DownloadWorksheetReportParams, 'request'> &
  DownloadWorksheetReportParams['request']
export type CasReportPdfParams = Omit<DownloadCasAutoTestReportParams, 'request'> &
  DownloadCasAutoTestReportParams['request']
/** 그룹 보고서 생성이 끝나면 알림으로 오는 산출물 id */
export type GroupReportFileParams = { uuid: string }

export const getLegacyReports = async (
  reportIds: LegacyReport['id'][],
  meta?: RequestMeta,
): Promise<LegacyReport[]> => {
  const body = await httpClient().get<CommonResponseListReportResponse>('/report/ids', {
    params: { reportIds },
    meta,
  })
  return adaptLegacyReports(body.data) ?? []
}

export const getLegacyReport = async (
  studentId: StudentId,
  reportId: LegacyReport['id'],
  meta?: RequestMeta,
): Promise<LegacyReport> => {
  const body = await httpClient().get<CommonResponseReportResponse>(
    `/report/${studentId}/${reportId}`,
    { meta },
  )
  return adaptLegacyReport(body.data)
}

/** 학생의 달별 월별 보고서 생성 가능 여부 */
export const getMonthlyReportAvailabilities = async (
  studentId: StudentId,
  revision: Revision,
  meta?: RequestMeta,
): Promise<MonthlyReportAvailability[]> => {
  const body = await httpClient().get<CommonResponseListMonthlyAvailableReportResponse>(
    `/report/${studentId}/monthly-available`,
    { params: { revision }, meta },
  )
  return adaptMonthlyReportAvailabilities(body.data) ?? []
}

/** 같은 기간에 이미 만든 보고서가 있어 다시 만들어지는지 */
export const checkLegacyReportDuplication = async (
  params: LegacyReportDuplicationParams,
  meta?: RequestMeta,
): Promise<LegacyReportSaveResult> => {
  const body = await httpClient().get<CommonResponseReportCreateResponse>('/report/duplicate', {
    params,
    meta,
  })
  return adaptLegacyReportSaveResult(body.data)
}

export const getRecentReportYearMonth = async (
  params: RecentReportParams,
  config?: Pick<RequestConfig, 'meta' | 'signal'>,
): Promise<RecentReportYearMonth> => {
  const body = await httpClient().get<CommonResponseRecentlyReportResponse>('/report/recently', {
    params,
    ...config,
  })
  return adaptRecentReportYearMonth(body.data)
}

/** 기간 안에 보고서로 만들 학습 내역이 있는 학생 */
export const getReportCreatableStudentIds = async (
  params: ReportCreatableStudentParams,
  meta?: RequestMeta,
): Promise<StudentId[]> => {
  const body = await httpClient().get<CommonResponseSetString>('/report/group/student', {
    params,
    meta,
  })
  return adaptReportStudentIds(body.data) ?? []
}

export const createLegacyReports = async (
  body: LegacyReportCreateBody,
  meta?: RequestMeta,
): Promise<LegacyReportSaveResult> =>
  adaptLegacyReportSaveResult(
    (await httpClient().post<CommonResponseReportCreateResponse>('/report', body, { meta })).data,
  )

export const updateLegacyReport = async (
  studentId: StudentId,
  reportId: LegacyReport['id'],
  body: LegacyReportBody,
  meta?: RequestMeta,
): Promise<LegacyReportSaveResult> =>
  adaptLegacyReportSaveResult(
    (
      await httpClient().put<CommonResponseReportCreateResponse>(
        `/report/${studentId}/${reportId}`,
        body,
        { meta },
      )
    ).data,
  )

export const deleteLegacyReports = async (
  reportIds: LegacyReport['id'][],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete('/report', { params: { reportIds }, meta })
}

/** 학부모앱 공개 전환. sending 이면 공개하면서 문자를 보낸다 */
export const updateLegacyReportOpen = async (
  body: ReportOpenBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch('/report/open', body, { meta })
}

/** 보고서들을 한 파일로 내려받을 주소 */
export const getReportsFile = async (
  params: ReportsFileParams,
  meta?: RequestMeta,
): Promise<ReportFile> =>
  adaptReportFile(
    await httpClient().get<CommonCloudFrontUrlResponse>('/report/v2/download', { params, meta }),
  )

/***************** 원클릭·시험 그룹 보고서 *****************/
// 생성 요청은 비동기라 응답 본문이 없다. 다 만들어지면 알림이 산출물 uuid 를 싣고 온다.

export const createWorksheetGroupReport = async (
  body: WorksheetGroupReportBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/report/worksheet/group', body, { meta })
}

export const createCasGroupReport = async (
  body: CasGroupReportBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/report/cas/group', body, { meta })
}

export const createExamGroupReport = async (
  body: ExamGroupReportBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post('/report/exam/group', body, { meta })
}

export const getWorksheetGroupReportFile = async (
  params: GroupReportFileParams,
  meta?: RequestMeta,
): Promise<ReportFile> =>
  adaptReportFile(
    await httpClient().get<CommonCloudFrontUrlResponse>('/report/worksheet/v2/group/download', {
      params,
      meta,
    }),
  )

export const getCasGroupReportFile = async (
  params: GroupReportFileParams,
  meta?: RequestMeta,
): Promise<ReportFile> =>
  adaptReportFile(
    await httpClient().get<CommonCloudFrontUrlResponse>('/report/cas/v2/group/download', {
      params,
      meta,
    }),
  )

export const getExamGroupReportFile = async (
  params: GroupReportFileParams,
  meta?: RequestMeta,
): Promise<ReportFile> =>
  adaptReportFile(
    await httpClient().get<CommonCloudFrontUrlResponse>('/report/exam/v2/group/download', {
      params,
      meta,
    }),
  )

/***************** 유형분석 보고서 *****************/
// 코멘트·교재·기출·추천 여부는 서버가 nullable 로, 표지 여부는 기본값(false)으로 받는다. 2026-10-13 백엔드
// develop(cc5f60e17) 의 report/controller/request/StudentAchievementReportRequest.kt 로 확인.
export type AchievementReportCreateBody = WithDefaultedOptionals<
  WithNullableOptionals<StudentAchievementReportRequest>,
  'coverFlag'
>
export type AchievementReportFileParams = DownloadAchievementReportParams

export const getStudentAchievementReports = async (
  studentId: StudentId,
  meta?: RequestMeta,
): Promise<StudentAchievementReports> => {
  const body = await httpClient().get<CommonResponseStudentAchievementReportResponse>(
    `/report/achievement/${studentId}`,
    { meta },
  )
  return adaptStudentAchievementReports(body.data)
}

export const createAchievementReport = async (
  studentId: StudentId,
  report: AchievementReportCreateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<CommonResponse>(`/report/achievement/${studentId}`, report, { meta })
}

export const deleteAchievementReports = async (
  params: DeleteByReportIdParams,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<CommonResponse>('/report/achievement', { params, meta })
}

export const getAchievementReportFile = async (
  params: AchievementReportFileParams,
  meta?: RequestMeta,
): Promise<ReportFile> =>
  adaptReportFile(
    await httpClient().get<CommonCloudFrontUrlResponse>('/report/achievement/v2/download', {
      params,
      meta,
    }),
  )

/***************** AI 코멘트 (AI 서버) *****************/
// AI 서버('ai' 클라이언트)는 Swagger 가 없어 2026-10-14 기준 선생님 앱이 보내던 본문을 근거로 적었다.
// 코멘트를 스트리밍(SSE)으로 받는 요청은 이음매가 표현하지 못해 앱에 남는다.

/** 보고서 한 건의 기간 지표와 월별 추이. comparisonType 이 없으면 비교 집단 없이 코멘트를 쓴다 */
export type ReportAiCommentBody = {
  studentName: Student['name']
  comparisonType?: ReportComparisonGroup | null
  rank: ReportDocument['rank']
  correctRate: ReportDocument['correctRate']
  scoringCount: ReportDocument['scoringCount']
  level: ReportDocument['level']
  monthlyComponent: ReportDocument['monthlyComponent']
}

/** AI 서버 세션을 연다. 보고서 코멘트 스트리밍과 학생 AI 튜터가 같은 세션 요청을 쓴다 */
export const createAiSession = async (meta?: RequestMeta): Promise<ReportAiSession['sessionId']> =>
  adaptReportAiSession(await httpClient('ai').post<unknown>('/ai/session', undefined, { meta }))
    ?.sessionId

/** 보고서 지표로 AI 코멘트를 한 번에 받는다. 조건이 바뀌면 앞선 요청을 버리도록 signal 을 받는다 */
export const createReportAiComment = async (
  body: ReportAiCommentBody,
  signal?: AbortSignal,
  meta?: RequestMeta,
): Promise<ReportAiComment> =>
  adaptReportAiComment(
    await httpClient('ai').post<unknown>('/ai/report-comment', body, { signal, meta }),
  )

/***************** 원클릭 개인 보고서 PDF *****************/
export const getWorksheetReportPdf = async (
  params: WorksheetReportPdfParams,
  meta?: RequestMeta,
): Promise<HttpFile> => httpClient().getFile('/report/worksheet/download', { params, meta })

/** 자동시험 원클릭 개인 보고서 */
export const getCasReportPdf = async (
  params: CasReportPdfParams,
  meta?: RequestMeta,
): Promise<HttpFile> => httpClient().getFile('/report/cas/download', { params, meta })
